import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  useSyncExternalStore,
} from "react";
import { useParams } from "react-router";
import { useGlobalStacks } from "@/state/globalStacks";
import { DBRecordsLoader, EMPTY_SNAPSHOT } from "@/providers/dbRecordsLoader";
import { useAppDispatch, useAppSelector } from "@/state/redux/hooks";
import { store } from "@/state/redux/store";
import { selectViewingRecord, setViewingRecord } from "@/state/redux/slices/ui";
import { useBreakpoint, useDebouncedValue } from "@/common/hooks";
import {
  useCanonicalPath,
  useCollection,
  useCollections,
  useSpace,
  useSpaceIdentity,
  useSpaceUserID,
} from "@/api/hooks";
import { useGetNoteRecordsQuery } from "@/api/api";
import {
  useAutoSelectRecordsMode,
  useShowAllRecordsOnBoot,
} from "@/common/allRecordsRange";
import { useSwipeRef } from "@/common/swipe";
import { HGInterval, HGTimeRecordPlus } from "@/common/types";
import { recordMatches } from "@/common/record";
import {
  RecordTreeOptions,
  buildRecordTree,
  collectToggleKeys,
} from "@/common/recordTree";
import { buildDescendantsMap, scopedCollectionIds } from "@/common/utils";
import { applyTitleRecords } from "@/common/titleRecords";
import { useRecordsViewOptions } from "@/state/recordsView";
import { useDocumentTitle } from "@/common/useDocumentTitle";
import RecordPreview from "./RecordPreview";
import RecordPasteListener from "./RecordPasteListener";
import RecordNew from "./RecordNew";
import RecordsToolbar from "./RecordsToolbar";
import RecordsTimeline from "./RecordsTimeline";
import RecordsNotes from "./RecordsNotes";
import RecordsEmpty from "./RecordsEmpty";
import RecordsBand from "./RecordsBand";
import ProviderBand from "./ProviderBand";
import { useExternalSources } from "@/providers/external/useExternalSources";

const EMPTY_RECORDS: HGTimeRecordPlus[] = [];

function Message({ children }: { children: React.ReactNode }) {
  return (
    <div className="flex grow items-center justify-center">
      <p className="text-sm text-muted-foreground">{children}</p>
    </div>
  );
}

export default function Records() {
  // useGlobalStacks (not getGlobalStacks) so navigating the time hierarchy
  // rebuilds the tree instead of waiting for the loader to happen to notify.
  const globalStacks = useGlobalStacks();
  const dispatch = useAppDispatch();
  const viewingRecord = useAppSelector(selectViewingRecord);
  const { is2Xl } = useBreakpoint();
  // What is in the URL may be a nickname/slug or a raw id; the API accepts
  // both. Anything that needs a real id uses the resolved collection instead.
  const { collectionID: collectionRef } = useParams();
  const collection = useCollection();
  const collectionID = collection?.id;
  const { isOwn, owner } = useSpace();
  const { name: spaceName } = useSpaceIdentity();
  // The loader's target, and it has to be the *id* wherever one is known —
  // which is exactly what useSpaceUserID resolves. Keyed off the URL segment
  // instead, the canonical redirect from /{id} to /{nickname} read as a
  // different user, wiping the record cache and refetching the same records
  // under the other spelling.
  const currentUserID = useSpaceUserID();
  // On a collection page the collection's canonical path already spells out
  // both segments; redirecting to the user's would drop the collection.
  useCanonicalPath(
    collectionRef ? collection?.canonical_path : owner?.canonical_path,
  );
  // A collection link opened cold shows everything that collection has: the
  // range fits its records, or the notes tab opens when it has none on the time
  // axis. Only on the page the app booted at — picking collections inside a
  // running app keeps the interval.
  useShowAllRecordsOnBoot();
  // And whichever tab the collection actually has records in opens, however the
  // page was reached — a collection with nothing on the time axis would
  // otherwise open an empty timeline just because the last one was left there.
  useAutoSelectRecordsMode();

  // The browser tab names the page: a collection by its title, someone else's
  // space by their name, and your own / all collections by the time on screen
  // (which is why this reads the live stacks). Undefined while a collection or
  // foreign user is still resolving — the hook shows the app name until then.
  useDocumentTitle(
    collectionRef
      ? collection?.title
      : !isOwn
        ? spaceName
        : globalStacks.getName(),
  );
  // A collection is in the URL but has not resolved yet. Loading now would
  // briefly fetch every collection's records and then throw them away.
  const resolvingCollection = !!collectionRef && !collectionID;
  const pasteCollectionIDs = useMemo(
    () => (collectionID ? [collectionID] : []),
    [collectionID],
  );

  const { options, setOption } = useRecordsViewOptions();
  // Notes belong to a collection, so a stored "notes" mode falls back to the
  // timeline outside one. Derived here once and handed to the toolbar, which
  // must not re-derive it or the two views drift apart.
  const notesMode = options.mode === "notes" && !!collectionRef;
  // Notes have no place on the time axis, so the swipe that shifts the cursor
  // is off there -- it would move a position the list below never reflects.
  const swipeRef = useSwipeRef(!notesMode);
  // Keys whose open/shut state differs from the default for their kind, so a
  // rebuilt tree keeps whatever the user opened without having to be re-seeded.
  const [toggled, setToggled] = useState<Set<string>>(new Set());
  // Transient — unlike RecordsViewOptions, a search shouldn't survive a reload.
  const [query, setQuery] = useState("");
  // The input stays instant; the filter → fold → tree → render chain behind it
  // is far too expensive to run on every keystroke.
  const trimmedQuery = useDebouncedValue(query.trim().toLowerCase(), 150);

  const toggleKey = useCallback((key: string) => {
    setToggled((previous) => {
      const next = new Set(previous);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }, []);

  useEffect(() => {
    if (!is2Xl) dispatch(setViewingRecord(null));
  }, [is2Xl, dispatch]);

  /// loader
  const loader = useRef<DBRecordsLoader | undefined>(undefined);
  if (!loader.current) loader.current = new DBRecordsLoader();

  // Stable identities: useSyncExternalStore re-subscribes whenever `subscribe`
  // changes, which a closure rebuilt every render makes it do on every render.
  const subscribeToLoader = useCallback(
    (callback: () => void) => loader.current?.subscribe(callback) ?? (() => {}),
    [],
  );
  const getLoaderSnapshot = useCallback(
    () => loader.current?.getSnapshot() ?? EMPTY_SNAPSHOT,
    [],
  );
  const snapshot = useSyncExternalStore(subscribeToLoader, getLoaderSnapshot);

  useEffect(() => {
    if (resolvingCollection) return;
    if (currentUserID) loader.current?.setTarget(currentUserID, collectionID);
  }, [currentUserID, collectionID, resolvingCollection]);

  // The provider collections in scope. The loader compares these by value, so
  // handing it a fresh array every render costs nothing and a config edit
  // invalidates exactly the intervals it produced.
  const externalSources = useExternalSources(collectionID);
  useEffect(() => {
    loader.current?.setExternalSources(externalSources);
  }, [externalSources]);

  useEffect(() => {
    const current = loader.current;
    return () => current?.destroy();
  }, []);

  const { data: noteRecords } = useGetNoteRecordsQuery(
    { userID: currentUserID, collectionID },
    { skip: !currentUserID || !notesMode || !collectionID },
  );

  const filteredNoteRecords = useMemo(() => {
    const source = noteRecords ?? EMPTY_RECORDS;
    return trimmedQuery
      ? source.filter((r) => recordMatches(r, trimmedQuery))
      : source;
  }, [noteRecords, trimmedQuery]);

  // getInterval() hands back a fresh object every call. Pinning both to their
  // values here keeps the identity stable, so the tree memo below can depend on
  // them directly and stay lint-checked as it grows.
  const currentInterval = globalStacks.getInterval();
  const currentEndInterval = globalStacks.getEndInterval();
  const intervalKey = `${currentInterval.it}:${currentInterval.n}`;
  const endIntervalKey = currentEndInterval
    ? `${currentEndInterval.it}:${currentEndInterval.n}`
    : "";
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const interval = useMemo(() => currentInterval, [intervalKey]);
  const endInterval = useMemo(
    () => currentEndInterval,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [endIntervalKey],
  );

  const titleRecordID = collection?.title_record?.id;
  useEffect(() => {
    // Moving in time (or to another space) drops the preview, because what it
    // shows has just scrolled off the list behind it. The one exception is the
    // collection's own title record: like the band it comes from, it is not in
    // that list and does not belong to the range on screen, so it stays.
    if (
      titleRecordID &&
      store.getState().ui.viewingRecord?.id === titleRecordID
    ) {
      return;
    }
    dispatch(setViewingRecord(null));
  }, [
    intervalKey,
    endIntervalKey,
    currentUserID,
    collectionID,
    titleRecordID,
    dispatch,
  ]);

  // The tree is built against the interval the records on screen were loaded
  // for, not the one the cursor is on. While a request is in flight the two
  // differ, and pairing the new interval with the old records would bucket
  // every row into a place it does not belong. The toolbar and the header
  // above read the live stacks, so the cursor still moves immediately and only
  // the list lags.
  const treeInterval = snapshot.interval ?? interval;
  const treeEndInterval = snapshot.interval
    ? snapshot.endInterval
    : endInterval;
  const rootName = snapshot.interval ? snapshot.name : globalStacks.getName();
  const records = snapshot.records;

  const filteredRecords = useMemo(
    () =>
      trimmedQuery
        ? records.filter(
            // A count row has no text to match, and leaving it in would make a
            // search look like it had found something it cannot open.
            (r) => !r.external?.summary && recordMatches(r, trimmedQuery),
          )
        : records,
    [records, trimmedQuery],
  );

  // On a collection page the backend hands back the whole descendant subtree.
  // Narrow that to the collection itself, or the collection plus its immediate
  // child collections, when the toolbar asks for it. "all" and non-collection
  // pages pass through untouched.
  const scopedIDs = useMemo(
    () =>
      scopedCollectionIds(collectionID, collection, options.collectionScope),
    [collectionID, collection, options.collectionScope],
  );

  const scopedRecords = useMemo(() => {
    if (!scopedIDs) return filteredRecords;
    const allowed = new Set<string>(scopedIDs);
    return filteredRecords.filter((r) =>
      r.collection_id.some((id) => allowed.has(id)),
    );
  }, [filteredRecords, scopedIDs]);

  // Resolved once per collection list rather than per candidate pair: the
  // title-record pass asks "is this record anywhere under that collection"
  // often enough that walking the hierarchy each time would show.
  const collections = useCollections();
  const descendantsOf = useMemo(
    () => buildDescendantsMap(collections),
    [collections],
  );

  // A search has to be able to reach a record that is normally folded into the
  // one heading its collection, so filtering suspends the fold entirely — the
  // same reasoning as `effectiveToggled` forcing every level open below.
  const groupedRecords = useMemo(
    () =>
      trimmedQuery
        ? scopedRecords
        : applyTitleRecords(scopedRecords, descendantsOf, collectionID),
    [scopedRecords, descendantsOf, collectionID, trimmedQuery],
  );

  // Narrowed to the fields the tree actually reads. `setRecordsViewOption`
  // hands back a whole new options object, so depending on it wholesale would
  // rebuild every record's place in the tree when the toolbar is merely folded
  // away or the notes sort is changed.
  const treeOptions = useMemo<RecordTreeOptions>(
    () => ({
      groupBy: options.groupBy,
      thenBy: options.thenBy,
      order: options.order,
      spanning: options.spanning,
      showGaps: options.showGaps,
    }),
    [
      options.groupBy,
      options.thenBy,
      options.order,
      options.spanning,
      options.showGaps,
    ],
  );

  const tree = useMemo(
    () =>
      buildRecordTree(
        groupedRecords,
        treeInterval,
        treeEndInterval,
        rootName,
        treeOptions,
      ),
    [groupedRecords, treeInterval, treeEndInterval, rootName, treeOptions],
  );

  // While filtering, force every group open so no match hides inside a
  // collapsed level; the user's own collapse state comes back untouched once
  // the query is cleared.
  const effectiveToggled = useMemo(
    () => (trimmedQuery ? new Set<string>() : toggled),
    [trimmedQuery, toggled],
  );

  const handleNavigate = useCallback(
    (target: HGInterval) => globalStacks.setIntervals(target, null),
    [globalStacks],
  );

  const collapseAll = useCallback(
    () => setToggled(new Set(collectToggleKeys(tree))),
    [tree],
  );
  const expandAll = useCallback(() => setToggled(new Set()), []);

  const noMatchMessage = `No records matching “${query.trim()}”`;
  // Dim only when there is something to dim: on a cold start the body is the
  // loading message, and fading that would just make it hard to read.
  const showStale = snapshot.pending && snapshot.loaded && !notesMode;

  let body: React.ReactNode;
  if (!currentUserID) {
    body = <Message>No records</Message>;
  } else if (notesMode) {
    body = (
      <RecordsNotes
        records={filteredNoteRecords}
        sort={options.notesSort}
        order={options.notesOrder}
        collectionID={collectionID}
        emptyMessage={
          trimmedQuery ? noMatchMessage : "No notes in this collection"
        }
      />
    );
  } else if (!snapshot.loaded) {
    // Only the cold start still shows this. Every later navigation keeps the
    // previous list on screen, dimmed, until the new one arrives.
    body = <Message>Loading records…</Message>;
  } else if (tree.total === 0) {
    let emptyMessage = `No records in ${rootName}`;
    // if (collectionID && options.collectionScope === "direct")
    //   emptyMessage = "No records directly in this collection";
    // else if (collectionID && options.collectionScope === "children")
    //   emptyMessage = "No records in this collection or its subcollections";
    body = (
      <RecordsEmpty
        message={trimmedQuery ? noMatchMessage : emptyMessage}
        enabled={!!collectionID && !trimmedQuery}
        userID={currentUserID}
        collectionID={collectionID}
        collectionIDs={scopedIDs}
        onNavigate={handleNavigate}
      />
    );
  } else {
    body = (
      <RecordsTimeline
        tree={tree}
        toggled={effectiveToggled}
        toggle={toggleKey}
        onNavigate={handleNavigate}
        collectionID={collectionID}
      />
    );
  }

  const content = (
    <div className="flex h-full min-h-0 w-full min-w-0 flex-col" ref={swipeRef}>
      <RecordPasteListener collectionIDs={pasteCollectionIDs} />
      {/* Outside the scroll container on purpose. The record that heads a
          collection does not belong to it, so it is never in the list below,
          and its own date may sit outside the range being viewed — putting it
          in the flow would mean it sometimes simply is not there. Rendered
          unconditionally and always in this position: it holds a ref that must
          not be reset by a remount. */}
      <RecordsBand
        collection={collection}
        // Null until the list it counts exists, so a cold start does not read
        // "0 records" under the loading message.
        count={
          notesMode
            ? noteRecords
              ? filteredNoteRecords.length
              : null
            : snapshot.loaded
              ? tree.total
              : null
        }
        query={query}
        setQuery={setQuery}
        notesMode={notesMode}
        showModeSwitch={!!collectionRef}
        options={options}
        setOption={setOption}
      />
      <ProviderBand statuses={snapshot.externalStatus} />
      {options.toolbarOpen && (
        <RecordsToolbar
          options={options}
          setOption={setOption}
          interval={interval}
          endInterval={endInterval}
          notesMode={notesMode}
          inCollection={!!collectionRef}
          onCollapseAll={collapseAll}
          onExpandAll={expandAll}
        />
      )}
      {/* Above the list rather than in it, and outside the scroll container, so
          it reads the same on both tabs and stays reachable however far down
          the list the user has gone. */}
      <RecordNew mode={notesMode ? "notes" : "time"} />
      {/* The one signal that a request is out. The list below stays put and
          keeps its DOM; a round trip short enough that this bar barely appears
          reads as no transition at all, which is the point. */}
      <div className="relative h-[2px] shrink-0 overflow-hidden">
        {showStale && <div className="records-progress absolute inset-y-0" />}
      </div>
      <div
        className={`noscroll relative flex min-h-0 grow flex-col overflow-y-auto ${
          showStale ? "pointer-events-none opacity-55" : ""
        }`}
      >
        {body}
      </div>
    </div>
  );

  if (is2Xl) {
    return (
      <div className="flex h-full w-full">
        <div className="flex h-full w-1/2 flex-col border">{content}</div>
        <div className="flex h-full w-1/2 flex-col">
          {viewingRecord ? (
            <RecordPreview />
          ) : (
            <Message>Select a record to view</Message>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="prioritize-second-child h-full w-full">
      <div className="flex h-full w-full flex-col border">{content}</div>
      {viewingRecord && (
        <div className="h-full w-full">
          <RecordPreview />
        </div>
      )}
    </div>
  );
}
